/**
 * @fileoverview MongoDB monitoring service — inherently-online data access for
 *   the dedicated MongoDB dashboard (cluster → database → collection drill-down).
 * @module system-web/services/mongoDbMonitoringService
 *
 * Responsibilities:
 * - Provide typed wrappers over the read-only /api/system/mongodb routes.
 * - Own the (recognized) online-only API access for live monitoring data.
 *
 * Dependencies:
 * - apiClient.apiFetch (bearer-token attach + 401 refresh).
 *
 * Architecture:
 * - Local-first EXCEPTION: this is live operational monitoring data, not user
 *   data — the same inherently-online bypass already used by
 *   systemStatusService.ts. Dexie is intentionally NOT the source of truth here;
 *   data is fetched on demand and never persisted. Hooks call these functions;
 *   components never call the API directly.
 *
 * Notes:
 * - Responses never contain cluster connection strings or credentials.
 * - Database / collection names are URL-encoded before being sent.
 *
 * @localFirst
 * Source of Truth: server (live, non-persisted monitoring data).
 * Write Flow:      none — read-only.
 * Sync Strategy:   on-demand fetch per drill-down expand; no Dexie caching.
 * Conflict Resolution: n/a (read-only).
 */

import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// Types — mirror the backend mongoMonitoringService DTOs
// ---------------------------------------------------------------------------

export type ClusterKind = 'system' | 'tenant';

/**
 * @contract ClusterListItem
 * Purpose: One row in the cluster list (system + tenant clusters).
 * Fields:  id, name, kind, region, status, maxDatabases, currentDatabaseCount.
 * Rules:   `id` is 'system' or a registry ObjectId hex; no connection strings.
 * Sync:    n/a (live monitoring data).
 */
export interface ClusterListItem {
  id: string;
  name: string;
  kind: ClusterKind;
  region: string | null;
  status: string | null;
  maxDatabases: number | null;
  currentDatabaseCount: number | null;
}

export interface ClusterListResponse {
  clusters: ClusterListItem[];
  collectedAt: string;
}

export interface ServerStatusSummary {
  uptime: number;
  uptimeFormatted: string;
  version: string;
  host: string;
  connections: { current: number; available: number; totalCreated: number };
  opcounters: { insert: number; query: number; update: number; delete: number; command: number };
  network: { bytesIn: number; bytesOut: number };
  mem: { resident: number; virtual: number };
  replication: { isReplSet: boolean; setName: string | null };
}

export interface AtlasNode {
  host: string;
  role: string;
  cpuPercent: number;
  memPercent: number;
  diskUsedPercent: number;
  replicationLagSeconds: number;
  networkKBytesIn: number;
  networkKBytesOut: number;
}

export interface AtlasClusterMetrics {
  clusterName: string;
  tier: string;
  state: string;
  region: string;
  nodes: AtlasNode[];
}

export interface DatabaseListItem {
  name: string;
  sizeOnDisk: number | null;
  empty: boolean;
}

/**
 * @contract ClusterDetail
 * Purpose: Per-cluster detail (serverStatus + database list + Atlas metrics).
 * Fields:  id, name, kind, serverStatus, databases, atlas, collectedAt, error.
 * Rules:   serverStatus null + `error` set means the cluster was unreachable.
 * Sync:    n/a (live monitoring data).
 */
export interface ClusterDetail {
  id: string;
  name: string;
  kind: ClusterKind;
  serverStatus: ServerStatusSummary | null;
  databases: DatabaseListItem[];
  atlas: AtlasClusterMetrics | null;
  collectedAt: string;
  error: string | null;
}

export interface DbStatsSummary {
  collections: number;
  documents: number;
  dataSize: number;
  storageSize: number;
  indexSize: number;
  indexes: number;
}

export interface CollectionListItem {
  name: string;
  type: string;
}

/**
 * @contract DatabaseDetail
 * Purpose: Per-database detail (dbStats + collection list).
 * Fields:  cluster, database, stats, collections, collectedAt, error.
 * Rules:   collection-level stats loaded separately (on demand).
 * Sync:    n/a (live monitoring data).
 */
export interface DatabaseDetail {
  cluster: string;
  database: string;
  stats: DbStatsSummary | null;
  collections: CollectionListItem[];
  collectedAt: string;
  error: string | null;
}

export interface IndexDetail {
  name: string;
  keys: Record<string, unknown>;
  unique: boolean;
  sizeBytes: number | null;
}

export interface CollStatsSummary {
  documents: number;
  dataSize: number;
  storageSize: number;
  totalIndexSize: number;
  avgObjSize: number;
  capped: boolean;
  nindexes: number;
}

/**
 * @contract CollectionDetail
 * Purpose: Per-collection detail (collStats + index details).
 * Fields:  cluster, database, collection, stats, indexes, collectedAt, error.
 * Rules:   stats null + `error` set means collStats failed.
 * Sync:    n/a (live monitoring data).
 */
export interface CollectionDetail {
  cluster: string;
  database: string;
  collection: string;
  stats: CollStatsSummary | null;
  indexes: IndexDetail[];
  collectedAt: string;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Service functions — real-time data; valid Dexie bypass (inherently online)
// ---------------------------------------------------------------------------

export async function listClusters(): Promise<ClusterListResponse> {
  return apiFetch('/api/system/mongodb/clusters') as Promise<ClusterListResponse>;
}

export async function getClusterDetail(clusterId: string): Promise<ClusterDetail> {
  return apiFetch(`/api/system/mongodb/clusters/${encodeURIComponent(clusterId)}`) as Promise<ClusterDetail>;
}

export async function getDatabaseDetail(clusterId: string, dbName: string): Promise<DatabaseDetail> {
  return apiFetch(
    `/api/system/mongodb/clusters/${encodeURIComponent(clusterId)}/databases/${encodeURIComponent(dbName)}`,
  ) as Promise<DatabaseDetail>;
}

export async function getCollectionDetail(
  clusterId: string,
  dbName: string,
  collName: string,
): Promise<CollectionDetail> {
  return apiFetch(
    `/api/system/mongodb/clusters/${encodeURIComponent(clusterId)}/databases/${encodeURIComponent(
      dbName,
    )}/collections/${encodeURIComponent(collName)}`,
  ) as Promise<CollectionDetail>;
}
